export type GeometryType = 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon';

export interface ShapefileData {
  type: 'FeatureCollection';
  features: any[];
  crs?: {
    type: string;
    properties: {
      name: string;
    };
  };
}

export interface ProjectionOption {
  epsg: number;
  name: string;
  proj4?: string;
  popular?: boolean;
}

export const COMMON_PROJECTIONS: ProjectionOption[] = [
  // Common Projections
  {
    epsg: 4326,
    name: 'WGS 84 / Geographic (Latitude, Longitude)',
    proj4: '+proj=longlat +datum=WGS84 +no_defs',
    popular: true
  },
  {
    epsg: 3857,
    name: 'Web Mercator / Pseudo-Mercator (Google Maps, OpenStreetMap)',
    proj4: '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs',
    popular: true
  },
  
  // UTM Northern Hemisphere
  {
    epsg: 32646,
    name: 'WGS 84 / UTM Zone 46N (Aceh Utara)',
    proj4: '+proj=utm +zone=46 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32647,
    name: 'WGS 84 / UTM Zone 47N (Sumatera Utara)',
    proj4: '+proj=utm +zone=47 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32648,
    name: 'WGS 84 / UTM Zone 48N (Kalimantan Utara)',
    proj4: '+proj=utm +zone=48 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32649,
    name: 'WGS 84 / UTM Zone 49N (Sulawesi Utara)',
    proj4: '+proj=utm +zone=49 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32650,
    name: 'WGS 84 / UTM Zone 50N (Maluku Utara)',
    proj4: '+proj=utm +zone=50 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32651,
    name: 'WGS 84 / UTM Zone 51N (Papua Utara)',
    proj4: '+proj=utm +zone=51 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32652,
    name: 'WGS 84 / UTM Zone 52N (Papua Tengah Utara)',
    proj4: '+proj=utm +zone=52 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32653,
    name: 'WGS 84 / UTM Zone 53N (Papua Timur Utara)',
    proj4: '+proj=utm +zone=53 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 32654,
    name: 'WGS 84 / UTM Zone 54N (Papua Timur)',
    proj4: '+proj=utm +zone=54 +datum=WGS84 +units=m +no_defs'
  },
  
  // Indonesia UTM Zones - Southern Hemisphere (46S-54S) - POPULAR
  {
    epsg: 32746,
    name: 'WGS 84 / UTM Zone 46S (Aceh)',
    proj4: '+proj=utm +zone=46 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32747,
    name: 'WGS 84 / UTM Zone 47S (Sumatera Barat, Riau)',
    proj4: '+proj=utm +zone=47 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32748,
    name: 'WGS 84 / UTM Zone 48S (Jakarta, Jawa Barat, Lampung)',
    proj4: '+proj=utm +zone=48 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32749,
    name: 'WGS 84 / UTM Zone 49S (Jawa Timur, Bali, NTB)',
    proj4: '+proj=utm +zone=49 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32750,
    name: 'WGS 84 / UTM Zone 50S (Kalimantan Barat, NTT)',
    proj4: '+proj=utm +zone=50 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32751,
    name: 'WGS 84 / UTM Zone 51S (Sulawesi, Kalimantan Timur)',
    proj4: '+proj=utm +zone=51 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32752,
    name: 'WGS 84 / UTM Zone 52S (Maluku, Papua Barat)',
    proj4: '+proj=utm +zone=52 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32753,
    name: 'WGS 84 / UTM Zone 53S (Papua)',
    proj4: '+proj=utm +zone=53 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  {
    epsg: 32754,
    name: 'WGS 84 / UTM Zone 54S (Papua Timur)',
    proj4: '+proj=utm +zone=54 +south +datum=WGS84 +units=m +no_defs',
    popular: true
  },
  
  // Indonesia DGN95 TM-3 Zones (Complete)
  {
    epsg: 23830,
    name: 'DGN95 / Indonesia TM-3 Zone 46.2 (Aceh)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=99 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23831,
    name: 'DGN95 / Indonesia TM-3 Zone 47.1 (Sumatera Utara)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=102 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23832,
    name: 'DGN95 / Indonesia TM-3 Zone 47.2 (Riau, Jambi)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=105 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23833,
    name: 'DGN95 / Indonesia TM-3 Zone 48.1 (Lampung, Bengkulu)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=108 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23834,
    name: 'DGN95 / Indonesia TM-3 Zone 48.2 (Jakarta, Jawa Barat)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=111 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23835,
    name: 'DGN95 / Indonesia TM-3 Zone 49.1 (Jawa Tengah)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=114 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23836,
    name: 'DGN95 / Indonesia TM-3 Zone 49.2 (Jawa Timur, Bali)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=117 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23837,
    name: 'DGN95 / Indonesia TM-3 Zone 50.1 (NTB, NTT)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=120 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23838,
    name: 'DGN95 / Indonesia TM-3 Zone 50.2 (Kalimantan Barat)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=123 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23839,
    name: 'DGN95 / Indonesia TM-3 Zone 51.1 (Kalimantan Tengah)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=126 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23840,
    name: 'DGN95 / Indonesia TM-3 Zone 51.2 (Sulawesi Utara)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=129 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23841,
    name: 'DGN95 / Indonesia TM-3 Zone 52.1 (Maluku)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=132 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23842,
    name: 'DGN95 / Indonesia TM-3 Zone 52.2 (Papua Barat)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=135 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23843,
    name: 'DGN95 / Indonesia TM-3 Zone 53.1 (Papua)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=138 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23844,
    name: 'DGN95 / Indonesia TM-3 Zone 53.2 (Papua Tengah)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=141 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  {
    epsg: 23845,
    name: 'DGN95 / Indonesia TM-3 Zone 54.1 (Papua Timur)',
    proj4: '+proj=tmerc +lat_0=0 +lon_0=144 +k=0.9999 +x_0=200000 +y_0=1500000 +ellps=WGS84 +units=m +no_defs'
  },
  
  // Other Common
  {
    epsg: 4269,
    name: 'NAD83 (North America)',
    proj4: '+proj=longlat +ellps=GRS80 +datum=NAD83 +no_defs'
  },
  {
    epsg: 32633,
    name: 'WGS 84 / UTM Zone 33N',
    proj4: '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs'
  },
  {
    epsg: 2154,
    name: 'RGF93 / Lambert-93 (France)',
    proj4: '+proj=lcc +lat_1=49 +lat_2=44 +lat_0=46.5 +lon_0=3 +x_0=700000 +y_0=6600000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  },
  {
    epsg: 27700,
    name: 'OSGB 1936 / British National Grid',
    proj4: '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs'
  }
];

/** Linear unit a CRS measures in — what CAD formats need to label a drawing. */
export type ProjectionUnit = 'degree' | 'metre' | 'foot' | 'unknown';

/** Unit declared by a `.prj` WKT string, which is the most reliable source. */
export function unitFromPrj(prj: string | undefined): ProjectionUnit {
  if (!prj) return 'unknown';
  if (/PROJCS/i.test(prj) === false && /GEOGCS/i.test(prj)) return 'degree';
  const unit = prj.match(/UNIT\s*\[\s*"([^"]+)"/gi)?.pop()?.match(/"([^"]+)"/)?.[1];
  if (!unit) return 'unknown';
  const u = unit.toLowerCase();
  if (/degree|grad|radian/.test(u)) return 'degree';
  if (/met(er|re)/.test(u)) return 'metre';
  if (/f(oo|ee)t|ft/.test(u)) return 'foot';
  return 'unknown';
}

/**
 * Unit of a CRS this app knows about. Codes outside the table fall back to
 * metres, which is true of the overwhelming majority of projected systems —
 * pass the `.prj` to `unitFromPrj` when the exact answer matters.
 */
export function projectionUnit(epsg: number): ProjectionUnit {
  const proj = COMMON_PROJECTIONS.find((p) => p.epsg === epsg)?.proj4;
  if (!proj) return epsg === 4326 || epsg === 4269 ? 'degree' : 'metre';
  if (/\+proj=longlat/.test(proj)) return 'degree';
  const unit = proj.match(/\+units=([\w-]+)/)?.[1]?.toLowerCase();
  if (!unit) return 'unknown';
  if (unit === 'm') return 'metre';
  if (/ft|foot/.test(unit)) return 'foot';
  if (/deg/.test(unit)) return 'degree';
  return 'unknown';
}
